import axios from 'axios';

const API_URL = process.env.REACT_APP_CUSTOMERS_API_URL;
const API_TOKEN = process.env.REACT_APP_CUSTOMERS_API_TOKEN;

// Create axios instance with default config
const customersApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_TOKEN}`,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  withCredentials: false // API uses token auth, not cookies
});

// Add response interceptor for error handling
customersApi.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // Server responded with error
      console.error('Customers API Error:', error.response.data);

      if (error.response.status === 401) {
        console.error('Invalid or expired API token');
      } else if (error.response.status === 429) {
        console.error('Rate limit exceeded:', error.response.data.resetIn);
      }
    } else if (error.request) {
      // Request made but no response
      console.error('No response from Customers API');
    } else {
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

export const customersApiService = {
  // ============================================================================
  // CUSTOMERS ENDPOINTS
  // ============================================================================

  getAllCustomers: async () => {
    const response = await customersApi.get('/customers');
    return response.data;
  },

  getCustomerById: async (id) => {
    const response = await customersApi.get(`/customers/${id}`);
    return response.data;
  },

  getCustomerByNumber: async (customerNumber) => {
    const response = await customersApi.get(`/customers/number/${customerNumber}`);
    return response.data;
  },

  createCustomer: async (customerData) => {
    const response = await customersApi.post('/customers', customerData);
    return response.data;
  },

  updateCustomer: async (id, customerData) => {
    const response = await customersApi.put(`/customers/${id}`, customerData);
    return response.data;
  },

  // ============================================================================
  // TICKETS ENDPOINTS
  // ============================================================================

  getAllTickets: async () => {
    const response = await customersApi.get('/tickets');
    return response.data;
  },

  getTicketById: async (id) => {
    const response = await customersApi.get(`/tickets/${id}`);
    return response.data;
  },

  getTicketByNumber: async (ticketNumber) => {
    const response = await customersApi.get(`/tickets/number/${ticketNumber}`);
    return response.data;
  },

  getCustomerWithTicket: async (ticketId) => {
    const response = await customersApi.get(`/tickets/${ticketId}/customer`);
    return response.data;
  },

  createTicket: async (ticketData) => {
    const response = await customersApi.post('/tickets', ticketData);
    return response.data;
  },

  updateTicket: async (id, ticketData) => {
    const response = await customersApi.put(`/tickets/${id}`, ticketData);
    return response.data;
  },

  deleteTicket: async (id) => {
    const response = await customersApi.delete(`/tickets/${id}`);
    return response.data;
  },

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  healthCheck: async () => {
    const response = await customersApi.get('/health');
    return response.data;
  }
};

export default customersApiService;
